/**
 * Build a ring - single page, step based ring builder.
 *
 * Step 1: start with a setting or a stone, filter by setting style / stone shape, pick a product.
 * Step 2: pick a compatible product from the step 1 product's "auto.link_for_matching_center" collection.
 * Step 3: review, choose variants (e.g. ring size) and add everything to the cart.
 *
 * Product lists are loaded with the Section Rendering API (sections/build-a-ring-products.liquid).
 */
class BuildARing extends HTMLElement {
  // Matches per_page in sections/build-a-ring-products.liquid
  static BATCH_SIZE = 24;

  connectedCallback() {
    this.config = JSON.parse(this.querySelector('[data-bar-config]').textContent);
    this.strings = this.config.strings;
    this.storageKey = `build-a-ring:${this.config.sectionId}`;
    this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.cache = new Map();
    this.buffer = [];
    this.pieceOptions = {};
    this.optionsReady = Promise.resolve();

    this.panels = {};
    this.querySelectorAll('[data-bar-panel]').forEach((panel) => {
      this.panels[panel.dataset.barPanel] = panel;
    });
    this.progress = this.querySelector('[data-bar-progress]');
    this.grid = this.querySelector('[data-bar-grid]');
    this.promotedEl = this.querySelector('[data-bar-promoted]');
    this.promotedGrid = this.querySelector('[data-bar-promoted-grid]');
    this.promotedIds = new Set();
    this.status = this.querySelector('[data-bar-status]');
    this.moreBtn = this.querySelector('[data-bar-more]');
    this.productsTitle = this.querySelector('[data-bar-products-title]');
    this.productsText = this.querySelector('[data-bar-products-text]');
    this.toolbar = this.querySelector('[data-bar-toolbar]');
    this.productsEl = this.querySelector('[data-bar-products]');
    this.filtersEl = this.querySelector('[data-bar-filters]');
    this.filtersBody = this.querySelector('[data-bar-filters-body]');
    this.filtersToggle = this.querySelector('[data-bar-filters-toggle]');
    this.filterCount = this.querySelector('[data-bar-filter-count]');
    this.sortSelect = this.querySelector('[data-bar-sort]');
    this.sortSelect.replaceChildren(...this.strings.sortOptions.map(([value, label]) => new Option(label, value)));
    const closeIcon = this.querySelector('[data-bar-icon-close]');
    this.closeIcon = closeIcon ? closeIcon.innerHTML : '';
    this.filterParams = new URLSearchParams();
    this.listKey = null;
    // Desktop: sidebar shown/hidden by the toggle; mobile: drawer
    this.desktopFilters = window.matchMedia('(min-width: 1000px)');
    this.filtersOpen = this.desktopFilters.matches;
    this.desktopFilters.addEventListener('change', (evt) => this.toggleFilters(evt.matches));
    this.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape' && this.filtersOpen && !this.desktopFilters.matches) {
        this.toggleFilters(false);
        this.filtersToggle.focus();
      }
    });
    this.reviewItems = this.querySelector('[data-bar-review-items]');
    this.totalEl = this.querySelector('[data-bar-total]');
    this.vipCta = this.querySelector('[data-bar-vip-cta]');
    this.errorEl = this.querySelector('[data-bar-error]');
    this.addBtn = this.querySelector('[data-bar-add]');
    this.wishlistEl = this.querySelector('[data-bar-wishlist]');
    this.wishlistStatus = this.querySelector('[data-bar-wishlist-status]');
    this.showWishlist = () => {
      if (this.wishlistEl) this.wishlistEl.hidden = !BuildARing.wishlistApp;
    };
    this.showWishlist();
    document.addEventListener('wk:app:ready', this.showWishlist);
    this.modal = this.querySelector('[data-bar-modal]');
    this.modalBody = this.querySelector('[data-bar-modal-body]');
    this.quickviewCache = new Map();

    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
    this.addEventListener('submit', (evt) => {
      if (!evt.target.matches('[data-bar-filter-form]')) return;
      evt.preventDefault();
      this.applyFilters();
    });
    this.modal.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      this.modalBody.replaceChildren();
      this.quickviewCard = null;
    });
    // Close when clicking the backdrop (the dialog element itself)
    this.modal.addEventListener('click', (evt) => {
      if (evt.target === this.modal) this.modal.close();
    });
    this.popstateHandler = (evt) => {
      if (evt.state && evt.state.buildARing) this.setState(evt.state.buildARing, { push: false });
    };
    window.addEventListener('popstate', this.popstateHandler);

    this.init();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.popstateHandler);
    document.removeEventListener('wk:app:ready', this.showWishlist);
  }

  async init() {
    let state = BuildARing.emptyState();
    const params = new URLSearchParams(window.location.search);
    const presetKeys = ['start', 'style', 'shape', 'setting', 'stone', 'config'];
    const hasPreset = presetKeys.some((key) => params.get(key));

    if (hasPreset) {
      state = await this.stateFromParams(params);
      // Drop the params so a refresh restores the in-progress build instead
      presetKeys.forEach((key) => params.delete(key));
      const query = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
    } else if (BuildARing.isReturnVisit()) {
      // Opening the page from a link starts over; refresh and back/forward restore the in-progress build
      try {
        state = (window.history.state && window.history.state.buildARing)
          || JSON.parse(sessionStorage.getItem(this.storageKey)) || state;
      } catch (e) {
        // Ignore unavailable/corrupt storage
      }
    }

    this.setState(state, { push: false, replace: true, scroll: false });

    if (hasPreset && state.start) {
      // Wait for images above the builder to load so the scroll target doesn't shift
      const scroll = () => this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (document.readyState === 'complete') {
        scroll();
      } else {
        window.addEventListener('load', scroll, { once: true });
      }
    }
  }

  /**
   * Builds the initial state from URL params so links can open the builder with steps preselected.
   * - start=setting|stone          Starting path (inferred from the other params when omitted)
   * - style={title} / shape={title} Style/shape button, matched by handleized title, e.g. "marquise-pear", "all-shapes"
   * - setting={handle}             Preselect a setting (step 1 when starting with a setting, otherwise step 2)
   * - stone={handle}               Preselect a stone (step 1 when starting with a stone, otherwise step 2)
   * - config={json}                Chosen variant and options per piece, see configUrl()
   * Unknown values are ignored and the builder stops at the last step that resolved.
   */
  async stateFromParams(params) {
    const state = BuildARing.emptyState();
    const settingHandle = params.get('setting');
    const stoneHandle = params.get('stone');
    let config = {};
    try {
      config = JSON.parse(params.get('config')) || {};
    } catch (e) {
      // Ignore a malformed config
    }

    let start = params.get('start');
    if (start !== 'setting' && start !== 'stone') {
      if (settingHandle || params.get('style')) {
        start = 'setting';
      } else if (stoneHandle || params.get('shape')) {
        start = 'stone';
      } else {
        return state;
      }
    }
    state.start = start;

    const category = params.get(start === 'setting' ? 'style' : 'shape');
    if (category) state.filter = this.findFilter(start, category);

    const [firstHandle, secondHandle] = start === 'setting' ? [settingHandle, stoneHandle] : [stoneHandle, settingHandle];
    if (firstHandle) {
      const [first, second] = await Promise.all([
        this.fetchProduct(firstHandle).catch(() => null),
        secondHandle ? this.fetchProduct(secondHandle).catch(() => null) : null
      ]);
      const secondType = start === 'setting' ? 'stone' : 'setting';
      state.first = BuildARing.applyConfig(first, config[start]);
      if (first) state.second = BuildARing.applyConfig(second, config[secondType]);
    }
    return state;
  }

  static applyConfig(product, saved) {
    if (!product || !saved || typeof saved !== 'object') return product;
    if (product.variants.some((variant) => variant.id === saved.variant)) product.variantId = saved.variant;
    product.selections = { options: saved.options, text: saved.text };
    return product;
  }

  // Link that reopens the review step with the same pieces, variants and options
  configUrl() {
    const { setting, stone } = this.pieces;
    const config = {};
    ['first', 'second'].forEach((key) => {
      const product = this.state[key];
      const options = this.pieceOptions[key];
      config[product === setting ? 'setting' : 'stone'] = {
        variant: BuildARing.selectedVariant(product).id,
        ...(options ? options.serialize() : product.selections)
      };
    });
    const params = new URLSearchParams({
      start: this.state.start,
      setting: setting.handle,
      stone: stone.handle,
      config: JSON.stringify(config)
    });
    return `${window.location.pathname}?${params}`;
  }

  // Matches a style/shape button by its title, e.g. "marquise-pear" for "Marquise + Pear"
  findFilter(start, value) {
    const wanted = BuildARing.handleize(value);
    const button = Array.from(this.querySelectorAll(`[data-bar-panel="filter-${start}"] [data-bar-filter]`))
      .find((btn) => BuildARing.handleize(btn.dataset.title || '') === wanted);
    if (!button) return null;
    return BuildARing.filterFromButton(button);
  }

  static filterFromButton(button) {
    const { url, title, icon, collectionId, boost } = button.dataset;
    return { url, title, icon, collectionId: collectionId || null, boost: boost ? boost.split('|') : [] };
  }

  static isReturnVisit() {
    const [nav] = performance.getEntriesByType ? performance.getEntriesByType('navigation') : [];
    return Boolean(nav) && (nav.type === 'reload' || nav.type === 'back_forward');
  }

  static emptyState() {
    return { start: null, filter: null, first: null, second: null };
  }

  /**
   * @param {object} state - New state.
   * @param {object} opts - push: add a history entry, replace: replace current entry, scroll: scroll to top of builder.
   */
  setState(state, { push = true, replace = false, scroll = push } = {}) {
    this.state = state;
    this.save();
    if (push) window.history.pushState({ ...window.history.state, buildARing: state }, '');
    if (replace) window.history.replaceState({ ...window.history.state, buildARing: state }, '');
    this.render();
    if (scroll && this.getBoundingClientRect().top < 0) {
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  update(patch) {
    this.setState({ ...this.state, ...patch });
  }

  // Saves in-place changes (variant, options) without adding a history entry
  persist() {
    this.save();
    window.history.replaceState({ ...window.history.state, buildARing: this.state }, '');
  }

  save() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (e) {
      // Storage full or disabled
    }
  }

  get secondType() {
    return this.state.start === 'setting' ? 'stone' : 'setting';
  }

  get view() {
    const { start, filter, first, second } = this.state;
    if (!start) return 'start';
    if (!first) return filter ? 'products' : `filter-${start}`;
    if (!second) return 'products';
    return 'review';
  }

  get currentStep() {
    if (this.view === 'review') return 3;
    return this.state.first ? 2 : 1;
  }

  render() {
    const { view } = this;
    Object.entries(this.panels).forEach(([name, panel]) => {
      panel.hidden = name !== view;
    });
    this.renderProgress();
    if (view === 'products') this.loadList();
    if (view === 'review') this.renderReview();
  }

  /* ---------- Events ---------- */

  handleClick(evt) {
    const quickviewLink = evt.target.closest('[data-bar-quickview]');
    if (quickviewLink && !(evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.button === 1)) {
      evt.preventDefault();
      this.openQuickview(quickviewLink.closest('[data-bar-card]'));
      return;
    }
    // The VIP popup opens via vip-loyalty.js; close the modal so it isn't covered
    if (evt.target.closest('[data-vip-signup-trigger]') && this.modal.open) {
      this.modal.close();
      return;
    }
    const filterLink = evt.target.closest('[data-bar-filter-remove], [data-bar-filter-clear]');
    if (filterLink) {
      evt.preventDefault();
      if (filterLink.hasAttribute('data-bar-filter-clear')) {
        this.removeFilter(null);
      } else {
        this.removeFilter(filterLink.dataset.barFilterRemove, filterLink.dataset.value);
      }
      return;
    }
    if (evt.target.closest('[data-bar-filters-close]')) {
      this.toggleFilters(false);
      return;
    }

    const target = evt.target.closest('button');
    if (!target || target.disabled) return;
    const { state } = this;

    if (target.hasAttribute('data-bar-modal-close')) {
      this.modal.close();
    } else if (target.hasAttribute('data-bar-qv-thumb')) {
      this.showQuickviewMedia(target);
    } else if (target.hasAttribute('data-bar-modal-select')) {
      const card = this.quickviewCard;
      this.modal.close();
      if (card) card.querySelector('[data-bar-select]').click();
    } else if (target.dataset.barStart) {
      this.update({ ...BuildARing.emptyState(), start: target.dataset.barStart });
    } else if (target.hasAttribute('data-bar-filter')) {
      this.update({ filter: BuildARing.filterFromButton(target), first: null, second: null });
    } else if (target.hasAttribute('data-bar-select')) {
      const product = BuildARing.readCardProduct(target.closest('[data-bar-card]'));
      if (!product) return;
      if (!state.first) {
        this.update({ first: product, second: null });
      } else {
        this.update({ second: product });
      }
    } else if (target.hasAttribute('data-bar-more')) {
      this.loadList(true);
    } else if (target.hasAttribute('data-bar-filters-toggle')) {
      this.toggleFilters();
    } else if (target.hasAttribute('data-bar-back')) {
      this.goBack();
    } else if (target.dataset.barEdit) {
      this.edit(target.dataset.barEdit);
    } else if (target.hasAttribute('data-bar-reset')) {
      this.setState(BuildARing.emptyState());
    } else if (target.hasAttribute('data-bar-progress-toggle')) {
      this.toggleProgress();
    } else if (target.hasAttribute('data-bar-wishlist-save')) {
      this.saveToWishlist(target);
    } else if (target.hasAttribute('data-bar-add')) {
      this.addToCart();
    }
  }

  handleChange(evt) {
    if (evt.target.closest('[data-bar-filter-form]') || evt.target === this.sortSelect) {
      if (evt.target.matches('[data-bar-single]') && evt.target.checked) {
        evt.target.form.querySelectorAll(`input[name="${CSS.escape(evt.target.name)}"]`).forEach((input) => {
          if (input !== evt.target) input.checked = false;
        });
      }
      // The price slider fires change events while dragging
      clearTimeout(this.filterTimer);
      this.filterTimer = setTimeout(() => this.applyFilters(), 400);
      return;
    }
    const select = evt.target.closest('[data-bar-variant]');
    if (!select) return;
    const key = select.dataset.barVariant;
    this.state[key] = { ...this.state[key], variantId: Number(select.value) };
    this.labelVariantOptions(select, this.state[key]);
    this.persist();
    this.updatePiecePrice(key);
    this.updateTotal();
  }

  goBack() {
    switch (this.view) {
      case 'review':
        this.update({ second: null });
        break;
      case 'products':
        this.update(this.state.first ? { first: null } : { filter: null });
        break;
      default:
        this.update({ start: null, filter: null });
    }
  }

  edit(step) {
    if (step === 'filter') this.update({ filter: null, first: null, second: null });
    if (step === 'first') this.update({ first: null, second: null });
    if (step === 'second') this.update({ second: null });
  }

  /* ---------- Quick view ---------- */

  async openQuickview(card) {
    const product = BuildARing.readCardProduct(card);
    if (!product) return;
    this.quickviewCard = card;
    this.modalBody.replaceChildren(BuildARing.el('div', 'bar-modal__loading'));
    document.documentElement.style.overflow = 'hidden';
    this.modal.showModal();

    try {
      const content = await this.fetchQuickview(product.handle);
      if (this.quickviewCard !== card) return;
      const node = content.cloneNode(true);
      node.querySelectorAll('[data-bar-modal-select]').forEach((btn) => {
        btn.textContent = btn.disabled ? this.strings.soldOut : this.strings.select;
      });
      this.modalBody.replaceChildren(node);
      this.fillQuickviewMetal(node, product.id);
      const title = node.querySelector('[data-bar-qv-title]');
      if (title) {
        title.id = `bar-qv-title-${this.config.sectionId}`;
        this.modal.setAttribute('aria-labelledby', title.id);
      }
    } catch (e) {
      if (this.quickviewCard === card) this.modalBody.replaceChildren(BuildARing.el('p', 'bar-status', this.strings.loadError));
    }
  }

  // Metal is usually an SC Product Options choice (e.g. Metal Type) rather than a variant option
  async fillQuickviewMetal(node, productId) {
    const row = node.querySelector('[data-bar-qv-metal]');
    if (!row) return;
    const data = await BarProductOptions.load(productId);
    const option = data && data.sets
      .flatMap((set) => set.options)
      .find((opt) => /metal/i.test(opt.public_name) && opt.options_values.length && !BarProductOptions.isText(opt));
    if (!option) return;
    row.querySelector('dd').textContent = option.options_values.map((value) => BarProductOptions.valueLabel(value)).join(', ');
    row.hidden = false;
  }

  fetchQuickview(handle) {
    if (!this.quickviewCache.has(handle)) {
      const url = new URL(`${this.root}products/${encodeURIComponent(handle)}`, window.location.origin);
      url.searchParams.set('section_id', 'build-a-ring-quickview');
      const request = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((html) => {
          const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bar-quickview-content]');
          if (!content) throw new Error('Missing quick view');
          return content;
        })
        .catch((error) => {
          this.quickviewCache.delete(handle);
          throw error;
        });
      this.quickviewCache.set(handle, request);
    }
    return this.quickviewCache.get(handle);
  }

  showQuickviewMedia(thumb) {
    const content = thumb.closest('[data-bar-quickview-content]');
    const template = thumb.nextElementSibling;
    if (!content || !template || !template.matches('[data-bar-qv-media]')) return;
    content.querySelector('[data-bar-qv-stage]').replaceChildren(template.content.cloneNode(true));
    content.querySelectorAll('[data-bar-qv-thumb]').forEach((el) => el.classList.toggle('is-active', el === thumb));
  }

  /* ---------- Progress bar ---------- */

  // Mobile only: the progress bar is a sticky, collapsible summary
  toggleProgress(open = !this.progress.classList.contains('is-open')) {
    this.progress.classList.toggle('is-open', open);
    this.progress.querySelector('[data-bar-progress-toggle]').setAttribute('aria-expanded', open);
  }

  renderProgress() {
    const { start, filter, first, second } = this.state;
    this.progress.hidden = !start;
    this.toggleProgress(false);
    if (!start) return;

    const s = this.strings;
    const labels = {
      1: start === 'setting' ? s.chooseSetting : s.chooseStone,
      2: this.secondType === 'setting' ? s.compatibleSetting : s.compatibleStone
    };

    const currentLabel = this.progress.querySelector(`[data-bar-step="${this.currentStep}"] [data-bar-step-label]`);
    this.progress.querySelector('[data-bar-toggle-step]').textContent = this.currentStep;
    this.progress.querySelector('[data-bar-toggle-label]').textContent = labels[this.currentStep] || currentLabel.textContent;
    this.progress.querySelector('[data-bar-toggle-thumbs]').replaceChildren(
      ...[first, second].filter((product) => product && product.image).map((product) => {
        const img = BuildARing.el('img', 'bar-progress__thumb');
        img.src = product.image;
        img.alt = '';
        return img;
      })
    );

    this.progress.querySelectorAll('[data-bar-step]').forEach((stepEl) => {
      const step = Number(stepEl.dataset.barStep);
      stepEl.classList.toggle('is-current', step === this.currentStep);
      stepEl.classList.toggle('is-complete', step < this.currentStep);
      if (step === this.currentStep) {
        stepEl.setAttribute('aria-current', 'step');
      } else {
        stepEl.removeAttribute('aria-current');
      }

      if (labels[step]) stepEl.querySelector('[data-bar-step-label]').textContent = labels[step];
      const summary = stepEl.querySelector('[data-bar-step-summary]');
      summary.replaceChildren();

      if (step === 1) {
        if (first) {
          summary.append(this.buildMiniItem(first, 'first'));
        } else {
          const choice = filter ? `${s[`${start}Label`]} · ${filter.title}` : s[`${start}Label`];
          if (filter && filter.icon) {
            const icon = BuildARing.el('img', 'bar-progress__icon');
            icon.src = filter.icon;
            icon.alt = '';
            summary.append(icon);
          }
          summary.append(BuildARing.el('span', 'bar-progress__choice', choice));
          if (filter) summary.append(this.buildEditButton('filter'));
        }
      } else if (step === 2 && second) {
        summary.append(this.buildMiniItem(second, 'second'));
      }
    });
  }

  buildMiniItem(product, editKey) {
    const item = BuildARing.el('div', 'bar-mini');
    if (product.image) {
      const img = BuildARing.el('img', 'bar-mini__img');
      img.src = product.image;
      img.alt = '';
      img.width = 48;
      img.height = 48;
      item.append(img);
    }
    const text = BuildARing.el('div', 'bar-mini__text');
    const price = BuildARing.el('span', 'bar-mini__price');
    price.append(...this.priceNodes(BuildARing.selectedVariant(product)));
    const meta = BuildARing.el('div', 'bar-mini__meta');
    meta.append(price, this.buildEditButton(editKey));
    text.append(BuildARing.el('span', 'bar-mini__title', product.title), meta);
    item.append(text);
    return item;
  }

  buildEditButton(key) {
    const btn = BuildARing.el('button', 'bar-link bar-mini__edit', this.strings.change);
    btn.type = 'button';
    btn.dataset.barEdit = key;
    return btn;
  }

  /* ---------- Boost filters ---------- */

  // Boost Product Filter & Search powers the collection page filters; lists use its API when the collection id is known
  useBoost(source) {
    return Boolean(this.config.boost && this.config.boost.shop && source.collectionId && !this.boostFailed);
  }

  applyFilters() {
    const params = new URLSearchParams();
    const form = this.filtersEl.querySelector('[data-bar-filter-form]');
    if (form) {
      form.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => params.append(input.name, input.value));
      form.querySelectorAll('[data-bar-range]').forEach((range) => {
        const [minInput, maxInput] = range.querySelectorAll('input');
        if (minInput.value === '' && maxInput.value === '') return;
        params.append(range.dataset.barRange, `${minInput.value || minInput.min || 0}:${maxInput.value || maxInput.max || 9999999}`);
      });
    }
    if (this.sortSelect.value) params.set('sort', this.sortSelect.value);
    this.filterParams = params;
    this.loadList();
  }

  // name null clears all filters (sorting is kept)
  removeFilter(name, value) {
    this.filterParams = new URLSearchParams(Array.from(this.filterParams).filter(([key, val]) => {
      if (key === 'sort') return true;
      if (name === null) return false;
      return !(key === name && val === value);
    }));
    this.loadList();
  }

  toggleFilters(open = !this.filtersOpen) {
    const drawer = !this.desktopFilters.matches;
    const opening = open && !this.filtersOpen;
    this.filtersOpen = open;
    this.productsEl.classList.toggle('is-filters-open', open);
    this.filtersToggle.setAttribute('aria-expanded', open);
    document.documentElement.classList.toggle('bar-filters-locked', open && drawer);
    if (opening && drawer) this.filtersEl.querySelector('[data-bar-filters-close]').focus();
  }

  // Boost values are raw tags, e.g. "era:Modern"; the option's prefix is a regex source
  static boostLabel(option, key) {
    let label = String(key);
    if (option.prefix) {
      try {
        label = label.replace(new RegExp(`^${option.prefix}`), '');
      } catch (e) {
        // Invalid prefix pattern
      }
    }
    return label.replace(/^[a-z_]+:(?=\S)/i, '').trim();
  }

  static FILTER_TICK = '<svg class="filter-tick" role="presentation" viewBox="0 0 20 20"><path d="M8.315 13.859l-3.182-3.417a.506.506 0 0 1 0-.684l.643-.683a.437.437 0 0 1 .642 0l2.22 2.393 4.942-5.327a.436.436 0 0 1 .643 0l.643.684a.504.504 0 0 1 0 .683l-5.91 6.35a.437.437 0 0 1-.642 0"></path></svg>';

  /**
   * Renders the Boost filter tree with the theme's filter markup (see snippets/faceted-filters.liquid).
   * @param {Array|null} options - filter.options from the Boost API; null hides filters (Shopify fallback).
   */
  renderFilters(options) {
    const s = this.strings;
    // Keep groups the shopper opened or closed as they were
    const openState = new Map(Array.from(this.filtersBody.querySelectorAll('.filter-group[data-bar-filter-param]')).map((group) => [
      group.dataset.barFilterParam,
      !group.querySelector('.toggle-target').classList.contains('toggle-target--hidden')
    ]));
    const chevron = this.querySelector('.bar-progress__toggle-icon').innerHTML;
    const form = BuildARing.el('form');
    form.dataset.barFilterForm = '';
    const applied = [];

    (options || []).filter((option) => option.status === 'active').forEach((option, index) => {
      const param = `${option.filterOptionId}[]`;
      const selected = this.filterParams.getAll(param);
      const container = BuildARing.el('div', 'toggle-target-container');
      let valueCount = 1;

      if (Array.isArray(option.values)) {
        const keys = option.values.map((value) => String(value.key));
        selected.forEach((key) => {
          if (!keys.includes(key)) keys.push(key);
        });
        if (!keys.length) return;
        valueCount = keys.length;
        keys.forEach((key) => {
          const label = BuildARing.boostLabel(option, key);
          const checked = selected.includes(key);
          const item = BuildARing.el('label', `filter-group__item${checked ? ' filter-group__item--active' : ''}`);
          const input = document.createElement('input');
          input.className = 'filter-group__checkbox';
          input.type = 'checkbox';
          input.name = param;
          input.value = key;
          input.checked = checked;
          if (option.selectType === 'single') input.dataset.barSingle = '';
          const text = BuildARing.el('span', 'filter-group__item__text', label);
          text.insertAdjacentHTML('beforeend', BuildARing.FILTER_TICK);
          item.append(input, text);
          container.append(item);
          if (checked) applied.push({ param, value: key, label });
        });
      } else {
        const isPrice = option.filterType === 'price';
        let { min, max } = option.values || {};
        // Boost doesn't always return price bounds; price inputs still work without them
        if (isPrice) {
          min = Math.floor(min || 0);
          max = max == null ? '' : Math.ceil(max);
        } else if (min == null || max == null) {
          return;
        }
        let [selMin = '', selMax = ''] = selected[0] ? selected[0].split(':') : [];
        if (Number(selMax) >= 9999999) selMax = '';
        // Markup from snippets/price-range.liquid; parsed so <price-range> upgrades with its children in place
        container.innerHTML = this.rangeMarkup(param, { min, max, selMin, selMax, isPrice, step: isPrice ? 1 : option.sliderStep || 'any' });
        if (selected[0]) {
          const text = isPrice
            ? `${BuildARing.formatMoney(selMin * 100)}${selMax === '' ? '+' : ` - ${BuildARing.formatMoney(selMax * 100)}`}`
            : `${selMin} - ${selMax}`;
          applied.push({ param, value: selected[0], label: `${option.label}: ${text}` });
        }
      }

      // Same as the collection page: long lists start collapsed unless something is selected
      const collapsed = openState.has(param) ? !openState.get(param) : valueCount > 12 && !selected.length;
      const targetClass = `bar-filter-group--${index}`;
      const group = BuildARing.el('div', 'filter-group');
      group.dataset.barFilterParam = param;
      const heading = document.createElement('toggle-target');
      heading.className = `filter-group__heading${collapsed ? ' toggle-target-toggler--is-hidden' : ''}`;
      heading.setAttribute('role', 'button');
      heading.tabIndex = 0;
      heading.dataset.toggleTarget = `.${targetClass}`;
      heading.append(BuildARing.el('div', 'filter-group__heading__text', option.label));
      heading.insertAdjacentHTML('beforeend', `<span class="filter-group__heading__indicator">${chevron}</span>`);
      const items = BuildARing.el('div', `filter-group__items ${targetClass} toggle-target${collapsed ? ' toggle-target--hidden' : ''}`);
      items.append(container);
      group.append(heading, items);
      form.append(group);
    });

    const nodes = [];
    if (applied.length) {
      const appliedGroup = BuildARing.el('div', 'filter-group filter-group--applied');
      const appliedHeading = BuildARing.el('div', 'filter-group__heading');
      appliedHeading.append(BuildARing.el('div', 'filter-group__heading__text', s.appliedFilters));
      const appliedItems = BuildARing.el('div', 'filter-group__items');
      applied.forEach(({ param, value, label }) => {
        // Links, as on the collection page (buttons pick up the theme's button styles)
        const link = BuildARing.el('a', 'filter-group__applied-item');
        link.href = '#';
        link.setAttribute('role', 'button');
        link.dataset.barFilterRemove = param;
        link.dataset.value = value;
        link.append(BuildARing.el('span', 'filter-group__applied-item__text', label));
        link.insertAdjacentHTML('beforeend', `<span class="filter-group__applied-item__icon">${this.closeIcon}</span>`);
        appliedItems.append(link);
      });
      const clearRow = BuildARing.el('div', 'filter-group__clear-row');
      const clear = BuildARing.el('a', 'filter-group__clear-link', s.clearAll);
      clear.href = '#';
      clear.setAttribute('role', 'button');
      clear.dataset.barFilterClear = '';
      clearRow.append(clear);
      appliedItems.append(clearRow);
      appliedGroup.append(appliedHeading, appliedItems);
      nodes.push(appliedGroup);
    }
    nodes.push(form);
    this.filtersBody.replaceChildren(...nodes);

    const hasFilters = Boolean(form.children.length || applied.length);
    this.filtersEl.hidden = !hasFilters;
    this.filtersToggle.hidden = !hasFilters;
    this.productsEl.classList.toggle('has-filters', hasFilters);
    this.toggleFilters(this.filtersOpen);
    this.filterCount.textContent = applied.length ? `(${applied.length})` : '';
    // Sorting also comes from Boost, so the toolbar is only shown for Boost lists
    this.toolbar.hidden = !options;
  }

  rangeMarkup(param, { min, max, selMin, selMax, isPrice, step }) {
    const s = this.strings;
    const esc = (value) => String(value).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
    const field = (bound, value, placeholder, label) => `
      <div class="cc-price-range__input-container">
        ${isPrice ? `<span class="cc-price-range__input-currency-symbol">${esc(s.currencySymbol)}</span>` : ''}
        <input class="cc-price-range__input cc-price-range__input--${bound}" type="text" inputmode="decimal"
          value="${esc(value)}" placeholder="${esc(placeholder)}" min="${esc(min)}" max="${esc(max)}" step="${esc(step)}" aria-label="${esc(label)}">
      </div>`;
    const inputs = `<div class="cc-price-range__input-row">${field('min', selMin, min, s.min)}${field('max', selMax, max, s.max)}</div>`;
    // The slider needs both bounds and works in whole numbers, so it's only used for price
    if (!isPrice || max === '') return `<div class="cc-price-range" data-bar-range="${esc(param)}">${inputs}</div>`;
    return `
      <price-range class="cc-price-range" data-bar-range="${esc(param)}">
        ${inputs}
        <div class="cc-price-range__bar">
          <div class="cc-price-range__bar-inactive"></div>
          <div class="cc-price-range__bar-active"></div>
          <div class="cc-price-range__control cc-price-range__control--min" aria-label="${esc(s.min)}"></div>
          <div class="cc-price-range__control cc-price-range__control--max" aria-label="${esc(s.max)}"></div>
        </div>
      </price-range>`;
  }

  /* ---------- Product lists ---------- */

  /**
   * The current list: Shopify collection path (fallback), Boost collection id and Boost values that
   * narrow it down (style/shape), matched as "any of".
   */
  listSource() {
    const { filter, first } = this.state;
    this.usingFallback = false;
    if (!first) return { path: filter.url, collectionId: filter.collectionId || null, base: filter.boost || [] };

    const matchPath = this.matchPath(first);
    if (matchPath) return { path: matchPath, collectionId: first.matchId || null, base: [] };

    // No matching collection metafield: fall back to the opposite collection filtered by shape tag
    this.usingFallback = true;
    const base = this.config.collections[this.secondType];
    const shape = first.shapes && first.shapes[0];
    return {
      path: shape ? `${base}/${BuildARing.handleize(`shopshape:${shape}`)}` : base,
      collectionId: this.config.collectionIds[this.secondType],
      base: shape ? [shape] : []
    };
  }

  matchPath(product) {
    if (!product.match) return null;
    try {
      let { pathname } = new URL(product.match, window.location.origin);
      if (!pathname.includes('/collections/')) return null;
      if (this.root !== '/' && !pathname.startsWith(this.root)) pathname = this.root + pathname.replace(/^\//, '');
      return pathname;
    } catch (e) {
      return null;
    }
  }

  async loadList(append = false) {
    const source = this.listSource();
    const token = Symbol('list');
    this.listToken = token;
    const s = this.strings;

    if (!append) {
      // A different list (step or style/shape) starts without filters
      const listKey = `${source.path}|${source.collectionId}`;
      if (listKey !== this.listKey) {
        this.listKey = listKey;
        this.filterParams = new URLSearchParams();
        this.sortSelect.value = '';
        this.toolbar.hidden = true;
        // Keep the sidebar (with placeholders) while the new list loads, so the grid doesn't jump between layouts
        this.filtersBody.replaceChildren(...Array.from({ length: 6 }, (_, i) => BuildARing.el('div', `bar-skeleton bar-skeleton--line${i % 3 === 2 ? ' bar-skeleton--short' : ''}`)));
        if (!this.desktopFilters.matches) this.toggleFilters(false);
      }
      this.page = 0;
      this.pages = Infinity;
      this.buffer = [];
      this.grid.replaceChildren(...BuildARing.skeletonCards(8));
      this.moreBtn.hidden = true;
      this.productsText.replaceChildren();
      this.productsTitle.textContent = this.state.first
        ? (this.secondType === 'setting' ? s.compatibleSetting : s.compatibleStone)
        : `${this.state.start === 'setting' ? s.chooseSetting : s.chooseStone}: ${this.state.filter.title}`;
      // Warm the first page while suggested products load, so both requests run in parallel
      this.fetchResults(source, 1).catch(() => {});
      this.promotedIds = await this.loadPromoted(source, token);
      if (token !== this.listToken) return;
    }

    this.status.textContent = '';
    this.classList.add('is-loading');
    this.grid.setAttribute('aria-busy', 'true');
    this.moreBtn.disabled = true;
    this.moreBtn.classList.add('is-loading');

    try {
      // Incompatible step 1 products are skipped, so pull extra pages until a full batch is buffered
      for (let fetches = 0; this.buffer.length < BuildARing.BATCH_SIZE && this.page < this.pages && fetches < 10; fetches += 1) {
        const result = await this.fetchResults(source, this.page + 1);
        if (token !== this.listToken) return;

        if (!append && fetches === 0) this.renderFilters(result.boostFilters || null);

        if (!append && fetches === 0 && this.state.first) {
          if (result.title && !this.usingFallback) this.productsTitle.textContent = result.title;
          if (this.usingFallback) {
            this.productsText.textContent = s.noMatch;
          } else if (result.description) {
            // Merchant content from the collection metaobject/description
            this.productsText.innerHTML = result.description;
          }
        }

        this.page = result.page;
        this.pages = result.pages;
        this.buffer.push(...result.cards.filter((card) => this.showCard(card) && !this.promotedIds.has(card.id)));
      }

      const batch = this.buffer.splice(0, BuildARing.BATCH_SIZE);
      this.clearSkeletons();
      this.grid.insertAdjacentHTML('beforeend', batch.map((card) => card.html).join(''));
      this.grid.querySelectorAll('[data-bar-select]').forEach((btn) => {
        btn.textContent = btn.disabled ? s.soldOut : s.select;
      });

      const hasMore = this.buffer.length > 0 || this.page < this.pages;
      if (!this.grid.children.length && !hasMore) this.status.textContent = s.noResults;
      this.moreBtn.hidden = !hasMore;
    } catch (e) {
      if (token === this.listToken) {
        this.clearSkeletons();
        this.status.textContent = s.loadError;
      }
    } finally {
      if (token === this.listToken) {
        this.classList.remove('is-loading');
        this.grid.removeAttribute('aria-busy');
        this.moreBtn.disabled = false;
        this.moreBtn.classList.remove('is-loading');
      }
    }
  }

  clearSkeletons() {
    this.grid.querySelectorAll('.bar-card--skeleton').forEach((el) => el.remove());
  }

  // Step 1 only lists pieces that have compatible matches; Boost results can include sold out pieces
  showCard(card) {
    return (this.state.first || card.compatible) && (!this.config.hideSoldOut || card.available);
  }

  /**
   * Renders products tagged with the promoted tag above the grid.
   * @returns {Promise<Set<string>>} Ids of the promoted products, so the main grid can skip them.
   */
  async loadPromoted(source, token) {
    const { promoted } = this.config;
    if (!promoted || !this.promotedEl) return new Set();
    this.promotedEl.hidden = true;
    this.promotedGrid.replaceChildren();

    try {
      const result = await this.fetchResults(source, 1, true);
      // Shopify redirects tag URLs to the plain collection when no product uses the tag
      if (token !== this.listToken || result.redirected) return new Set();
      const cards = result.cards
        .filter((card) => this.showCard(card))
        .slice(0, promoted.limit);
      if (!cards.length) return new Set();

      this.promotedGrid.insertAdjacentHTML('beforeend', cards.map((card) => card.html).join(''));
      this.promotedGrid.querySelectorAll('[data-bar-select]').forEach((btn) => {
        btn.textContent = btn.disabled ? this.strings.soldOut : this.strings.select;
      });
      this.promotedEl.hidden = false;
      return new Set(cards.map((card) => card.id));
    } catch (e) {
      return new Set();
    }
  }

  // Adds a tag to a collection URL path; a tag path segment combines with filter.p.tag params as AND
  static withTag(path, tag) {
    const url = new URL(path, window.location.origin);
    const [prefix, rest] = url.pathname.split('/collections/');
    const [handle, tags] = rest.split('/');
    url.pathname = `${prefix}/collections/${handle}/${tags ? `${tags}+${tag}` : tag}`;
    return `${url.pathname}${url.search}`;
  }

  static skeletonCards(count) {
    return Array.from({ length: count }, () => {
      const card = BuildARing.el('div', 'bar-card bar-card--skeleton');
      card.setAttribute('aria-hidden', 'true');
      card.append(
        BuildARing.el('div', 'bar-card__media bar-skeleton'),
        BuildARing.el('div', 'bar-skeleton bar-skeleton--line'),
        BuildARing.el('div', 'bar-skeleton bar-skeleton--line bar-skeleton--short')
      );
      return card;
    });
  }

  sectionUrl(path, page = 1) {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('section_id', this.config.productsSection);
    if (page > 1) url.searchParams.set('page', page);
    return url.toString();
  }

  /**
   * @param {object} source - From listSource().
   * @param {number} page
   * @param {boolean} promoted - Only products with the promoted tag.
   */
  fetchResults(source, page, promoted = false) {
    const nativePath = promoted ? BuildARing.withTag(source.path, this.config.promoted.tag) : source.path;
    if (!this.useBoost(source)) return this.fetchNativeResults(nativePath, page);
    return this.fetchBoostResults(source, page, promoted).catch(() => {
      // Boost unavailable: use Shopify collection pages (without filters) from now on
      this.boostFailed = true;
      return this.fetchNativeResults(nativePath, page);
    });
  }

  // all_products (used to render the cards) allows 20 handles per page
  static BOOST_LIMIT = 20;

  fetchBoostResults(source, page, promoted) {
    const { boost } = this.config;
    const url = new URL(boost.url);
    const params = url.searchParams;
    const limit = promoted ? Math.min(this.config.promoted.limit, BuildARing.BOOST_LIMIT) : BuildARing.BOOST_LIMIT;
    params.set('shop', boost.shop);
    params.set('collection_scope', source.collectionId);
    // Without these, the price filter and sorting match any market's price
    if (window.Shopify && Shopify.currency) params.set('currency', Shopify.currency.active.toLowerCase());
    if (window.Shopify && Shopify.country) params.set('country', Shopify.country);
    params.set('page', page);
    params.set('limit', limit);
    if (page === 1 && !promoted) params.set('build_filter_tree', 'true');
    // Own param names so they combine with the shopper's filters as AND
    source.base.forEach((value) => params.append('pf_t_bar_base[]', value));
    if (promoted) params.append('pf_t_bar_promoted[]', this.config.promoted.rawTag);
    this.filterParams.forEach((value, key) => params.append(key, value));

    const key = url.toString();
    if (!this.cache.has(key)) {
      const request = (async () => {
        const response = await fetch(key);
        if (!response.ok) throw new Error(response.status);
        const data = await response.json();
        const handles = (data.products || []).map((product) => product.handle).filter(Boolean);
        const cards = handles.length || (page === 1 && !promoted) ? await this.fetchCards(source, handles) : { cards: [] };
        return {
          ...cards,
          page,
          pages: Math.max(1, Math.ceil((data.total_product || 0) / limit)),
          boostFilters: promoted ? null : (data.filter && data.filter.options) || []
        };
      })().catch((error) => {
        this.cache.delete(key);
        throw error;
      });
      this.cache.set(key, request);
    }
    return this.cache.get(key);
  }

  // Boost results rendered with the theme's card (VIP prices, compatibility, product data), in Boost's order
  async fetchCards(source, handles) {
    const match = source.path.match(/\/collections\/([^/?#]+)/);
    const url = new URL(`${this.root}search`, window.location.origin);
    url.searchParams.set('q', `${match ? match[1] : '-'} ${handles.join(',')}`.trim());
    url.searchParams.set('section_id', this.config.productsSection);
    const response = await fetch(url);
    if (!response.ok) throw new Error(response.status);
    return BuildARing.parseResults(await response.text());
  }

  fetchNativeResults(path, page) {
    const requestUrl = new URL(this.sectionUrl(path, page));
    if (this.config.hideSoldOut) requestUrl.searchParams.set('filter.v.availability', '1');
    const url = requestUrl.toString();
    if (!this.cache.has(url)) {
      const request = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text().then((html) => ({ ...BuildARing.parseResults(html), redirected: response.redirected }));
        })
        .catch((error) => {
          this.cache.delete(url);
          throw error;
        });
      this.cache.set(url, request);
    }
    return this.cache.get(url);
  }

  async fetchProduct(handle) {
    const response = await fetch(this.sectionUrl(`${this.root}products/${encodeURIComponent(handle)}`));
    if (!response.ok) return null;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return BuildARing.readCardProduct(doc.querySelector('[data-bar-card]'));
  }

  static parseResults(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const results = doc.querySelector('[data-bar-results]');
    if (!results) throw new Error('Missing results');
    const titleTpl = results.querySelector('[data-bar-collection-title]');
    const descTpl = results.querySelector('[data-bar-collection-description]');
    return {
      page: Number(results.dataset.page) || 1,
      pages: Number(results.dataset.pages) || 1,
      title: titleTpl ? titleTpl.content.textContent.trim() : '',
      description: descTpl ? descTpl.innerHTML.trim() : '',
      cards: Array.from(results.querySelectorAll('[data-bar-card]')).map((card) => ({
        id: card.dataset.productId,
        html: card.outerHTML,
        compatible: card.dataset.barCompatible === 'true',
        available: !card.classList.contains('bar-card--sold-out')
      }))
    };
  }

  static readCardProduct(card) {
    const json = card && card.querySelector('[data-bar-product]');
    if (!json) return null;
    const product = JSON.parse(json.textContent);
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    product.variantId = variant.id;
    return product;
  }

  /* ---------- Review & cart ---------- */

  get pieces() {
    const { start, first, second } = this.state;
    return start === 'setting' ? { setting: first, stone: second } : { setting: second, stone: first };
  }

  renderReview() {
    const { first, second } = this.state;
    this.pieceOptions = {};
    this.errorEl.hidden = true;
    if (this.wishlistStatus) {
      this.wishlistStatus.hidden = true;
      const saveBtn = this.wishlistEl.querySelector('[data-bar-wishlist-save]');
      saveBtn.hidden = false;
      saveBtn.disabled = false;
    }
    this.reviewItems.replaceChildren(
      ...[['first', first], ['second', second]].map(([key, product]) => this.buildReviewItem(key, product))
    );
    this.updateTotal();
    this.loadPieceOptions();
  }

  loadPieceOptions() {
    const token = Symbol('review');
    this.reviewToken = token;
    this.pieceOptions = {};
    const finishing = this.config.finishing;

    this.optionsReady = Promise.all(['first', 'second'].map(async (key) => {
      const product = this.state[key];
      const data = await BarProductOptions.load(product.id);
      if (token !== this.reviewToken) return;
      const container = this.reviewItems.querySelector(`[data-bar-options="${key}"]`);
      if (!data || !data.sets.length) {
        if (container) container.replaceChildren();
        return;
      }

      const options = new BarProductOptions(data, {
        idPrefix: `bar-${this.config.sectionId}-${key}`,
        finishingVariantId: finishing ? finishing.id : null,
        selectFinishing: product === this.pieces.setting,
        saved: product.selections,
        onChange: () => {
          this.state[key] = { ...this.state[key], selections: options.serialize() };
          this.persist();
          this.updatePiecePrice(key);
          this.updateTotal();
        }
      });
      this.pieceOptions[key] = options;
      if (container) container.replaceChildren(options.render());
      this.updatePiecePrice(key);
      this.updateTotal();
    }));
  }

  buildReviewItem(key, product) {
    const type = product === this.pieces.setting ? 'setting' : 'stone';
    const li = BuildARing.el('li', 'bar-review__item');

    const media = BuildARing.el('a', 'bar-review__media');
    media.href = product.url;
    media.target = '_blank';
    media.rel = 'noopener';
    if (product.image) {
      const img = document.createElement('img');
      img.src = product.image;
      img.alt = product.title;
      img.loading = 'lazy';
      media.append(img);
    }

    const info = BuildARing.el('div', 'bar-review__info');
    info.append(
      BuildARing.el('span', 'bar-review__type', this.strings[`${type}Label`]),
      BuildARing.el('span', 'bar-review__title', product.title)
    );

    if (!product.hasOnlyDefaultVariant && product.variants.length > 1) {
      const id = `bar-variant-${this.config.sectionId}-${key}`;
      const label = BuildARing.el('label', 'bar-review__label', product.optionNames.join(' / '));
      label.htmlFor = id;
      const select = BuildARing.el('select', 'bar-review__select');
      select.id = id;
      select.dataset.barVariant = key;
      product.variants.forEach((variant) => {
        const option = new Option('', variant.id, false, variant.id === product.variantId);
        option.disabled = !variant.available;
        select.append(option);
      });
      this.labelVariantOptions(select, product);
      info.append(label, select);
    }

    const optionsEl = BuildARing.el('div', 'bar-review__options');
    optionsEl.dataset.barOptions = key;
    optionsEl.append(
      BuildARing.el('div', 'bar-skeleton bar-skeleton--line'),
      BuildARing.el('div', 'bar-skeleton bar-skeleton--line bar-skeleton--short')
    );
    info.append(optionsEl);

    const price = BuildARing.el('div', 'bar-review__price');
    price.dataset.barItemPrice = key;
    price.append(BuildARing.el('span', 'bar-review__amount'), BuildARing.el('ul', 'bar-review__extras'));

    li.append(media, info, price);
    this.updatePiecePrice(key, price);
    return li;
  }

  // The selected size shows no price since the piece price already reflects it
  labelVariantOptions(select, product) {
    const selected = BuildARing.selectedVariant(product);
    Array.from(select.options).forEach((option, index) => {
      const variant = product.variants[index];
      let text = variant.title;
      if (variant !== selected) text += ` - ${BuildARing.formatMoney(this.unitPrice(variant))}`;
      if (!variant.available) text += ` - ${this.strings.soldOut}`;
      option.textContent = text;
    });
  }

  // Piece price includes its selected options (e.g. platinum, engraving); setting labor is shown on its own line
  updatePiecePrice(key, priceEl = this.reviewItems.querySelector(`[data-bar-item-price="${key}"]`)) {
    if (!priceEl) return;
    const variant = BuildARing.selectedVariant(this.state[key]);
    const options = this.pieceOptions[key];
    const finishingId = this.config.finishing ? this.config.finishing.id : null;
    const extras = options ? options.priced().filter((item) => item.variantId !== finishingId) : [];
    const extrasTotal = extras.reduce((sum, item) => sum + item.price, 0);

    priceEl.querySelector('.bar-review__amount').replaceChildren(...this.priceNodes(variant, extrasTotal));
    priceEl.querySelector('.bar-review__extras').replaceChildren(
      ...extras.map((item) => BuildARing.el('li', '', `${item.name.replace(/%2C/g, ',')} +${BuildARing.formatMoney(item.price)}`))
    );
  }

  /* VIP customers (tagged "VIP") get the variant's powerx.custom_price; public-sale items (salePrice) apply to everyone. Discounts are applied at checkout. */
  unitPrice(variant) {
    if (variant.salePrice != null) return variant.salePrice;
    return this.config.vip.isVip && variant.vipPrice != null ? variant.vipPrice : variant.price;
  }

  priceNodes(variant, extra = 0) {
    const regular = extra ? BuildARing.formatMoney(variant.price + extra) : BuildARing.toText(variant.priceHtml);
    if (variant.salePrice != null) {
      return [
        BuildARing.el('span', 'vip-price__discounted', BuildARing.formatMoney(variant.salePrice + extra)),
        document.createTextNode(' '),
        BuildARing.el('s', 'vip-price__original', regular)
      ];
    }
    if (variant.vipPrice == null) return [document.createTextNode(regular)];

    if (this.config.vip.isVip) {
      const vip = extra ? BuildARing.formatMoney(variant.vipPrice + extra) : BuildARing.toText(variant.vipPriceHtml);
      return [
        BuildARing.el('span', 'vip-price__discounted', vip),
        document.createTextNode(' '),
        BuildARing.el('s', 'vip-price__original', regular)
      ];
    }

    // Opens the VIP signup popup via vip-loyalty.js
    const label = BuildARing.el('span', 'vip-price__label--vip-discount', this.config.vip.discountLabel);
    label.dataset.vipSignupTrigger = '';
    label.setAttribute('role', 'button');
    label.tabIndex = 0;
    return [document.createTextNode(regular), label];
  }

  get finishingCovered() {
    return Object.values(this.pieceOptions || {}).some((options) => options && options.coversFinishing);
  }

  // Mirrors the store's cart discount: free setting labor on rings over $2,000 (setting + stone + options)
  static FREE_LABOR_THRESHOLD = 200000;

  get priceBreakdown() {
    const { first, second } = this.state;
    const finishing = this.config.finishing;
    const labor = finishing && finishing.available ? finishing.price : 0;
    const optionsTotal = Object.values(this.pieceOptions || {}).reduce((sum, options) => sum + (options ? options.total() : 0), 0);
    // When SC Product Options adds the finishing fee it is part of the options total
    const options = optionsTotal - (this.finishingCovered ? labor : 0);
    const variants = [first, second].map((product) => BuildARing.selectedVariant(product));
    const pieces = variants.reduce((sum, variant) => sum + variant.price, 0);
    const piecesFinal = variants.reduce((sum, variant) => sum + this.unitPrice(variant), 0);
    const laborFree = labor > 0 && piecesFinal + options > BuildARing.FREE_LABOR_THRESHOLD;
    return { variants, pieces, piecesFinal, options, labor, laborFree };
  }

  updateTotal() {
    const { variants, pieces, piecesFinal, options, labor, laborFree } = this.priceBreakdown;
    const regular = pieces + options + labor;
    const final = piecesFinal + options + (laborFree ? 0 : labor);

    if (this.vipCta) this.vipCta.hidden = !variants.some((variant) => variant.vipPrice != null);

    const laborEl = this.querySelector('[data-bar-finishing-price]');
    if (laborEl && labor) {
      if (laborFree) {
        laborEl.replaceChildren(
          BuildARing.el('s', 'vip-price__original', BuildARing.formatMoney(labor)),
          document.createTextNode(' '),
          BuildARing.el('span', 'bar-review__free', this.strings.free)
        );
      } else {
        laborEl.textContent = BuildARing.formatMoney(labor);
      }
    }

    if (final < regular) {
      this.totalEl.replaceChildren(
        BuildARing.el('span', 'vip-price__discounted', BuildARing.formatMoney(final)),
        document.createTextNode(' '),
        BuildARing.el('s', 'vip-price__original', BuildARing.formatMoney(regular))
      );
    } else {
      this.totalEl.textContent = BuildARing.formatMoney(regular);
    }
  }

  async addToCart() {
    const { setting, stone } = this.pieces;
    const settingVariant = BuildARing.selectedVariant(setting);
    const stoneVariant = BuildARing.selectedVariant(stone);
    const s = this.strings;

    if (!settingVariant.available || !stoneVariant.available) {
      this.showError(s.soldOut);
      return;
    }

    this.addBtn.disabled = true;
    this.addBtn.classList.add('is-loading');
    this.errorEl.hidden = true;

    const items = await this.buildCartItems();
    if (!items) {
      this.showError(s.optionsRequired);
      this.addBtn.disabled = false;
      this.addBtn.classList.remove('is-loading');
      return;
    }

    try {
      const response = await fetch((window.theme && theme.routes.cartAdd) || `${this.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || s.loadError);

      if (this.priceBreakdown.laborFree) {
        await BuildARing.markFreeSetting(this.root, items[0].properties._build_a_ring_id).catch(() => {});
      }
      sessionStorage.removeItem(this.storageKey);
      window.location.href = (window.theme && theme.routes.cart) || `${this.root}cart`;
    } catch (error) {
      this.showError(error.message || s.loadError);
      this.addBtn.disabled = false;
      this.addBtn.classList.remove('is-loading');
    }
  }

  showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  /**
   * Cart attribute read by the free setting discount: comma-separated _build_a_ring_id values of rings over
   * FREE_LABOR_THRESHOLD. Their setting & finishing line carries the same _build_a_ring_id property.
   * Rings no longer in the cart are dropped (see the remove handler in assets/custom.js).
   */
  static FREE_SETTING_ATTRIBUTE = '_free_setting_rings';

  static async markFreeSetting(root, buildId) {
    const key = BuildARing.FREE_SETTING_ATTRIBUTE;
    const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
    const inCart = new Set(cart.items.map((item) => item.properties && item.properties._build_a_ring_id).filter(Boolean));
    const ids = String((cart.attributes && cart.attributes[key]) || '').split(',').filter((id) => id && inCart.has(id));
    if (!ids.includes(buildId)) ids.push(buildId);
    await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      // _free_setting: simple yes/no for discounts that can only compare a fixed value
      body: JSON.stringify({ attributes: { [key]: ids.join(','), _free_setting: 'yes' } })
    });
  }

  /**
   * Cart lines for the current ring: setting, stone, their SC option lines and the finishing fee.
   * @param {object} opts - mark: highlight missing required options.
   * @returns {Promise<Array|null>} null when required options are missing.
   */
  async buildCartItems({ mark = true } = {}) {
    await this.optionsReady;
    const { setting, stone } = this.pieces;
    const s = this.strings;
    const settingKey = this.state.start === 'setting' ? 'first' : 'second';
    const stoneKey = settingKey === 'first' ? 'second' : 'first';
    const settingOptions = this.pieceOptions[settingKey];
    const stoneOptions = this.pieceOptions[stoneKey];
    const optionsValid = [settingOptions, stoneOptions].map((options) => !options || options.validate(mark)).every(Boolean);
    if (!optionsValid) return null;

    const buildId = Date.now().toString(36);
    const props = (value, product) => ({
      [s.propertyName]: value,
      _build_a_ring_id: buildId,
      ...(product && product.publicSale ? { _public_sale: 'yes' } : {})
    });
    const lines = (options, variantId, properties) => (options
      ? options.cartLines(variantId, properties)
      : [{ id: variantId, quantity: 1, properties }]);
    const items = [
      ...lines(settingOptions, BuildARing.selectedVariant(setting).id, props(`${s.setWith} ${stone.title}`, setting)),
      ...lines(stoneOptions, BuildARing.selectedVariant(stone).id, props(`${s.setIn} ${setting.title}`, stone))
    ];
    if (this.config.finishing && this.config.finishing.available && !this.finishingCovered) {
      items.push({ id: this.config.finishing.id, quantity: 1, properties: props(`${setting.title} + ${stone.title}`) });
    }
    // Marks the setting & finishing line of a ring over FREE_LABOR_THRESHOLD for the free setting discount
    if (this.config.finishing && this.priceBreakdown.laborFree) {
      items.filter((item) => item.id === this.config.finishing.id).forEach((item) => {
        item.properties = { ...item.properties, _free_setting: 'yes' };
      });
    }
    return items;
  }

  // Swish (formerly Wishlist King) storefront SDK; it has a single wishlist of product/variant items, so the pair is saved as two items
  static get wishlistApp() {
    const app = window.WishlistKing;
    return app && app.apis && app.apis.wishlist && typeof app.apis.wishlist.addItem === 'function' ? app : null;
  }

  // Read by the Swish wishlist page (snippets/wishlist-page.liquid) to group saved pairs
  static WISHLIST_PAIRS_KEY = 'build-a-ring:wishlist-pairs';

  async saveToWishlist(button) {
    const app = BuildARing.wishlistApp;
    if (!app) return;
    const { setting, stone } = this.pieces;
    button.disabled = true;
    this.errorEl.hidden = true;

    try {
      // Calls the API directly: state.addWishlistItem() silently skips calls made while a previous add is still syncing
      const itemIds = {};
      for (const [key, product] of [['setting', setting], ['stone', stone]]) {
        const variantId = BuildARing.selectedVariant(product).id;
        const { wishlistItem, sessionId } = await app.apis.wishlist.addItem({ productId: product.id, variantId });
        if (sessionId && app.events) app.events.publish({ name: 'app:init-sid', data: { sessionId } });
        itemIds[key] = wishlistItem && wishlistItem.id;
        if (typeof app.state.publishEvent === 'function') {
          app.state.publishEvent({ eventName: 'wishlist:add:success', wishlistId: 'mine', productId: product.id, variantId });
        }
      }
      if (typeof app.state.revalidate === 'function') app.state.revalidate();

      this.rememberWishlistPair({
        setting: itemIds.setting ? String(itemIds.setting) : null,
        stone: itemIds.stone ? String(itemIds.stone) : null,
        settingProduct: String(setting.id),
        stoneProduct: String(stone.id),
        url: this.configUrl(),
        // Full configuration for the wishlist page's "Add ring to cart"; null when required options weren't chosen yet
        cartItems: await this.buildCartItems({ mark: false }),
        options: this.priceBreakdown.options,
        labor: this.priceBreakdown.labor,
        freeLaborThreshold: BuildARing.FREE_LABOR_THRESHOLD
      });
      button.hidden = true;
      this.wishlistStatus.hidden = false;
    } catch (e) {
      this.showError(this.strings.loadError);
      button.disabled = false;
    }
  }

  rememberWishlistPair(pair) {
    try {
      const pairs = JSON.parse(localStorage.getItem(BuildARing.WISHLIST_PAIRS_KEY)) || [];
      // Re-saving a ring already in the wishlist may not return item ids; update the existing pair for the same products
      const existing = pairs.find((p) => p.settingProduct === pair.settingProduct && p.stoneProduct === pair.stoneProduct);
      const merged = {
        ...pair,
        setting: pair.setting || (existing && existing.setting),
        stone: pair.stone || (existing && existing.stone)
      };
      if (!merged.setting || !merged.stone) return;
      const next = [
        merged,
        ...pairs.filter((p) => p !== existing && p.setting !== merged.setting && p.stone !== merged.stone)
      ].slice(0, 50);
      localStorage.setItem(BuildARing.WISHLIST_PAIRS_KEY, JSON.stringify(next));
    } catch (e) {
      // Storage unavailable: items are still saved, just not grouped
    }
  }

  /* ---------- Helpers ---------- */

  static selectedVariant(product) {
    return product.variants.find((v) => v.id === product.variantId) || product.variants[0];
  }

  static el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  static toText(html) {
    return new DOMParser().parseFromString(html || '', 'text/html').body.textContent.trim();
  }

  static handleize(str) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  static formatMoney(cents) {
    const currency = window.Shopify && Shopify.currency && Shopify.currency.active;
    if (!currency) return (cents / 100).toFixed(2);
    return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);
  }
}

/**
 * SC Product Options (formerly Bold Product Options) for one product.
 * Mirrors the app's cart format: option properties + _bold* properties on the product line,
 * and each priced option variant as its own line sharing the _boldBuilderId (see sections/main-cart.liquid).
 */
class BarProductOptions {
  static cache = new Map();

  static load(productId) {
    const shop = window.Shopify && window.Shopify.shop;
    if (!shop) return Promise.resolve(null);
    if (!BarProductOptions.cache.has(productId)) {
      const bold = window.BOLD && window.BOLD.common && window.BOLD.common.cacheParams;
      const url = new URL(`https://options.shopapps.site/v2/${shop}/generate_option/${productId}`);
      if (bold && bold.options) url.searchParams.set('tmp', bold.options);
      const request = fetch(url)
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => ({
          sets: BarProductOptions.stripHtml((data && data.option_product && data.option_product.option_sets) || []),
          emptyText: (data && data.settings && data.settings.frontend && data.settings.frontend.dropdown_empty_value) || '-- Choose {option} --'
        }))
        .catch(() => {
          BarProductOptions.cache.delete(productId);
          return null;
        });
      BarProductOptions.cache.set(productId, request);
    }
    return BarProductOptions.cache.get(productId);
  }

  // Option names and values can contain markup (e.g. "<b>Platinum</b>"); keep plain text for labels and cart properties
  static stripHtml(sets) {
    sets.forEach((set) => set.options.forEach((option) => {
      option.public_name = BuildARing.toText(option.public_name);
      option.additional_info = BuildARing.toText(option.additional_info);
      option.options_values.forEach((value) => {
        value.value = BuildARing.toText(value.value);
      });
      (option.conditions || []).forEach((condition) => condition.rules.forEach((rule) => {
        rule.value = BuildARing.toText(rule.value);
      }));
    }));
    return sets;
  }

  /**
   * @param {object} data - { sets, emptyText } from load().
   * @param {object} opts - idPrefix, finishingVariantId, selectFinishing, saved (from serialize()), onChange.
   */
  constructor(data, { idPrefix, finishingVariantId, selectFinishing, saved, onChange }) {
    this.idPrefix = idPrefix;
    this.emptyText = data.emptyText;
    this.onChange = onChange;
    this.selected = new Map();
    this.text = new Map();
    this.coversFinishing = false;
    this.entries = [];

    data.sets.forEach((set) => {
      // The builder always adds setting & finishing, so that option set is chosen automatically and not shown
      const finishingValue = finishingVariantId
        && set.options.flatMap((option) => option.options_values).find((value) => value.variant === finishingVariantId);

      set.options.forEach((option) => {
        const entry = { option, managed: Boolean(finishingValue), submit: false, wrap: null };
        const defaults = option.options_values.filter((value) => value.selected);
        const savedValues = saved && saved.options && saved.options[option.id];
        const savedText = saved && saved.text && saved.text[option.id];

        if (finishingValue && option.options_values.includes(finishingValue)) {
          if (selectFinishing) {
            this.selected.set(option.id, [finishingValue]);
            this.coversFinishing = true;
            entry.submit = true;
          }
        } else if (typeof savedText === 'string') {
          this.text.set(option.id, savedText);
        } else if (Array.isArray(savedValues)) {
          const values = option.options_values.filter((value) => savedValues.includes(value.value));
          this.selected.set(option.id, BarProductOptions.isMulti(option) ? values : values.slice(0, 1));
        } else if (defaults.length) {
          this.selected.set(option.id, BarProductOptions.isMulti(option) ? defaults : [defaults[0]]);
        }
        this.entries.push(entry);
      });
    });
  }

  // Selected values by text (sturdier than indexes if the option set is edited) and text inputs, keyed by option id
  serialize() {
    const options = {};
    const text = {};
    this.entries.forEach(({ option, managed }) => {
      if (managed || option.type === 'displaytext') return;
      if (BarProductOptions.isText(option)) {
        if (this.text.has(option.id)) text[option.id] = this.text.get(option.id);
      } else {
        options[option.id] = (this.selected.get(option.id) || []).map((value) => value.value);
      }
    });
    return { options, text };
  }

  static isMulti(option) {
    return option.type === 'checkboxmulti' || option.type === 'checkbox';
  }

  static isText(option) {
    return option.type === 'textbox' || option.type === 'textarea';
  }

  render() {
    const root = BuildARing.el('div', 'bar-options');
    this.entries.forEach((entry) => {
      if (entry.managed) return;
      entry.wrap = this.renderField(entry.option);
      if (entry.wrap) root.append(entry.wrap);
    });
    this.refresh(false);
    return root;
  }

  renderField(option) {
    const id = `${this.idPrefix}-${option.id}`;
    const wrap = BuildARing.el('div', `bar-option bar-option--${option.type}`);
    const labelText = `${option.public_name}${option.required ? ' *' : ''}`;
    const values = option.options_values;
    const current = this.selected.get(option.id) || [];

    switch (option.type) {
      case 'displaytext':
        wrap.append(BuildARing.el('p', 'bar-option__text', BuildARing.toText(values.map((value) => value.value).join(' '))));
        return wrap;

      case 'dropdown': {
        const label = BuildARing.el('label', 'bar-option__label', labelText);
        label.htmlFor = id;
        const select = BuildARing.el('select', 'bar-option__select');
        select.id = id;
        if (!current.length) select.append(new Option(this.emptyText.replace('{option}', option.public_name), ''));
        values.forEach((value, index) => select.append(new Option(BarProductOptions.valueLabel(value), index, false, current.includes(value))));
        select.addEventListener('change', () => {
          const value = values[select.value];
          this.set(option.id, value ? [value] : []);
        });
        wrap.append(label, select);
        break;
      }

      case 'radio':
      case 'swatch':
      case 'checkbox':
      case 'checkboxmulti': {
        const multi = BarProductOptions.isMulti(option);
        const label = BuildARing.el('span', 'bar-option__label', labelText);
        label.id = `${id}-label`;
        const group = BuildARing.el('div', 'bar-option__choices');
        group.setAttribute('role', multi ? 'group' : 'radiogroup');
        group.setAttribute('aria-labelledby', label.id);
        values.forEach((value) => {
          const choice = BuildARing.el('label', 'bar-option__choice');
          const input = document.createElement('input');
          input.type = multi ? 'checkbox' : 'radio';
          input.name = id;
          input.checked = current.includes(value);
          input.addEventListener('change', () => {
            const checked = Array.from(group.querySelectorAll('input')).map((el, i) => (el.checked ? values[i] : null)).filter(Boolean);
            this.set(option.id, checked);
          });
          choice.append(input);
          const color = value.swatch && value.swatch[0];
          if (option.type === 'swatch' && /^#[0-9a-f]{3,8}$/i.test(color || '')) {
            const chip = BuildARing.el('span', 'bar-option__swatch');
            chip.style.background = color;
            choice.append(chip);
          }
          choice.append(BuildARing.el('span', '', BarProductOptions.valueLabel(value)));
          group.append(choice);
        });
        wrap.append(label, group);
        break;
      }

      case 'textbox':
      case 'textarea': {
        const label = BuildARing.el('label', 'bar-option__label', labelText);
        label.htmlFor = id;
        const input = document.createElement(option.type === 'textarea' ? 'textarea' : 'input');
        input.className = 'bar-option__input';
        input.id = id;
        if (option.type === 'textbox') input.type = 'text';
        if (option.max_size > 0) input.maxLength = option.max_size;
        input.value = this.text.get(option.id) || '';
        input.addEventListener('input', () => {
          this.text.set(option.id, input.value);
          this.onChange();
        });
        wrap.append(label, input);
        break;
      }

      default:
        return null;
    }

    if (option.additional_info) wrap.append(BuildARing.el('p', 'bar-option__help', BuildARing.toText(option.additional_info)));
    return wrap;
  }

  static valueLabel(value) {
    return value.variant > 0 && value.price > 0 ? `${value.value} (+${BuildARing.formatMoney(value.price)})` : value.value;
  }

  set(optionId, values) {
    this.selected.set(optionId, values);
    this.refresh();
  }

  refresh(notify = true) {
    this.entries.forEach((entry) => {
      if (entry.wrap) entry.wrap.hidden = !this.isVisible(entry.option);
    });
    if (notify) this.onChange();
  }

  isVisible(option) {
    return (option.conditions || []).every((condition) => {
      const met = condition.rules.every((rule) => {
        // option_key format: {type}_{optionId}_{setId}
        const optionId = Number(rule.option_key.split('_')[1]);
        const has = (this.selected.get(optionId) || []).some((value) => value.value === rule.value);
        return rule.operator === 'not_equal' ? !has : has;
      });
      return condition.action === 'HIDE' ? !met : met;
    });
  }

  isActive(entry) {
    return entry.managed ? entry.submit : Boolean(entry.wrap) && this.isVisible(entry.option);
  }

  validate(mark = true) {
    let valid = true;
    this.entries.forEach((entry) => {
      if (!entry.wrap) return;
      const { option } = entry;
      let invalid = false;
      if (option.required && this.isActive(entry)) {
        invalid = BarProductOptions.isText(option)
          ? !(this.text.get(option.id) || '').trim()
          : !(this.selected.get(option.id) || []).length;
      }
      if (mark) entry.wrap.classList.toggle('is-invalid', invalid);
      if (invalid) valid = false;
    });
    return valid;
  }

  priced() {
    return this.entries
      .filter((entry) => this.isActive(entry))
      .flatMap((entry) => (this.selected.get(entry.option.id) || [])
        .filter((value) => value.variant > 0)
        .map((value) => ({
          variantId: value.variant,
          productId: value.product_id,
          price: value.price,
          name: (entry.option.type === 'checkbox' ? entry.option.public_name : value.value).replace(/,/g, '%2C')
        })));
  }

  total() {
    return this.priced().reduce((sum, item) => sum + item.price, 0);
  }

  properties() {
    const props = {};
    this.entries.forEach((entry) => {
      const { option } = entry;
      if (option.type === 'displaytext' || !this.isActive(entry)) return;
      if (BarProductOptions.isText(option)) {
        const text = (this.text.get(option.id) || '').trim();
        if (text) props[option.public_name] = text;
        return;
      }
      const values = this.selected.get(option.id) || [];
      if (!values.length) return;
      props[option.public_name] = option.type === 'checkbox' ? '✓' : values.map((value) => value.value).join(', ');
    });
    return props;
  }

  cartLines(variantId, baseProperties) {
    const priced = this.priced();
    const properties = { ...baseProperties, ...this.properties() };
    const lines = [{ id: variantId, quantity: 1, properties }];
    if (!priced.length) return lines;

    const builderId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    Object.assign(properties, {
      _boldVariantNames: priced.map((item) => item.name).join(','),
      _boldVariantIds: priced.map((item) => item.variantId).join(','),
      _boldProductIds: priced.map((item) => item.productId).join(','),
      _boldVariantPrices: priced.map((item) => item.price).join(','),
      _boldBuilderId: builderId
    });
    priced.forEach((item) => lines.push({
      id: item.variantId,
      quantity: 1,
      properties: { _boldBuilderId: builderId, _build_a_ring_id: baseProperties._build_a_ring_id }
    }));
    return lines;
  }
}

// Defined last: upgrading runs connectedCallback synchronously, which may use BarProductOptions right away
if (!customElements.get('build-a-ring')) customElements.define('build-a-ring', BuildARing);
